import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './driver.css';
import { enterSandbox, isSandbox, leaveSandbox, sandboxIds } from '@/sandbox/mode';
import { GUIDES, isGuideId } from './registry';
import type { Guide, GuideStep } from './types';

/**
 * Runs a tour in the sandbox. Each step is prepared before it shows: go to
 * its page, click what opens it, fill what it talks about, then wait for
 * its anchor. Steps whose anchor never appears (a button one role cannot
 * see) are skipped. Done or the X ends the tour, leaves the sandbox and
 * returns the person to where they started it.
 */

const MOUNT_WAIT_MS = 2_500;
const MOUNT_POLL_MS = 80;
const SETTLE_MS = 250;

export type Navigate = (path: string) => void;

/** Tour diagnostics, for support: which step prepared, and why one was skipped. */
function log(message: string, detail?: unknown): void {
  if (detail === undefined) console.debug(`[tour] ${message}`);
  else console.debug(`[tour] ${message}`, detail);
}

function selector(target: string): string {
  return `[data-guide="${target}"]`;
}

function find(target: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selector(target));
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** A real press on the control itself: Radix menus open on pointerdown, buttons on click. */
function press(anchor: HTMLElement): void {
  const element = anchor.matches('button, a, input, [role="button"], [role="menuitem"], [role="tab"]') ? anchor : (anchor.querySelector<HTMLElement>('button, a, input, [role="button"], [role="menuitem"], [role="tab"]') ?? anchor);
  const init = { bubbles: true, cancelable: true, button: 0, buttons: 1, pointerType: 'mouse', isPrimary: true } as const;
  element.dispatchEvent(new PointerEvent('pointerdown', init));
  element.dispatchEvent(new MouseEvent('mousedown', init));
  element.dispatchEvent(new PointerEvent('pointerup', { ...init, buttons: 0 }));
  element.dispatchEvent(new MouseEvent('mouseup', { ...init, buttons: 0 }));
  element.click();
}

/**
 * Radix closes a dialog or menu on a pointer-down or focus outside it. The
 * tour's popover and overlay are outside, so while a tour runs those events
 * are stopped before Radix's document listeners see them: only the tour's
 * own buttons move things along, and only its X ends it.
 */
function shieldPopover(): () => void {
  const guard = (event: Event): void => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.driver-popover, .driver-overlay')) event.stopPropagation();
    // driver.js closes the tour on an overlay click; the X is the only way out.
    if (event.type === 'click' && event.target.closest('.driver-overlay')) event.stopImmediatePropagation();
  };
  const types = ['pointerdown', 'mousedown', 'focusin', 'click'];
  for (const type of types) document.addEventListener(type, guard, true);
  return () => {
    for (const type of types) document.removeEventListener(type, guard, true);
  };
}

/** The path without `guide` or `sandbox` parameters, so it can be returned to quietly. */
export function withoutTourParams(path: string): string {
  const [pathname = '', search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.delete('guide');
  params.delete('sandbox');
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

/** A path with its `:eventId`, `:organizationId` and other placeholders filled from the sandbox. */
export function guidePath(path: string): string {
  const ids: Readonly<Record<string, string>> = sandboxIds();
  return path.replace(/:([A-Za-z]+)/g, (placeholder, key: string) => ids[key] ?? placeholder);
}

async function waitFor(target: string): Promise<HTMLElement | null> {
  const started = Date.now();
  for (;;) {
    const element = find(target);
    if (element) return element;
    if (Date.now() - started > MOUNT_WAIT_MS) return null;
    await pause(MOUNT_POLL_MS);
  }
}

function samePlace(path: string): boolean {
  const [pathname = '', search = ''] = path.split('?');
  if (window.location.pathname !== pathname) return false;
  const wanted = new URLSearchParams(search);
  const current = new URLSearchParams(window.location.search);
  return [...wanted.entries()].every(([key, value]) => current.get(key) === value);
}

/** Sets a controlled input's value the way React notices. */
function typeInto(target: string, text: string): void {
  const root = find(target);
  if (!root) return;
  const editable = root.matches('[contenteditable="true"]') ? root : root.querySelector<HTMLElement>('[contenteditable="true"]');
  if (editable) {
    editable.focus();
    document.execCommand('selectAll', false);
    document.execCommand('insertText', false, text);
    return;
  }
  const input = root.matches('input, textarea') ? (root as HTMLInputElement) : root.querySelector<HTMLInputElement | HTMLTextAreaElement>('input:not([type="checkbox"]):not([type="radio"]), textarea');
  if (!input) return;
  const prototype = input instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  input.focus();
  setter?.call(input, text);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function selectIn(target: string, value: string): void {
  const root = find(target);
  const select = root?.matches('select') ? (root as HTMLSelectElement) : root?.querySelector<HTMLSelectElement>('select');
  if (!select) return;
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  setter?.call(select, value);
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

/** A day as the date picker names it (`yyyy-mm-dd`), counted from today in the viewer's time zone. */
export function dayFromToday(daysFromToday: number, today: Date = new Date()): string {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysFromToday);
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
}

/** Picks an option by its label in the search-and-pick list under an anchor: types it, then clicks the match. */
async function chooseOption(target: string, label: string): Promise<void> {
  const root = await waitFor(target);
  const input = root?.querySelector<HTMLInputElement>('input[role="combobox"]');
  if (!root || !input) return;
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  input.focus();
  setter?.call(input, label);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  await pause(SETTLE_MS);
  const wanted = label.toLowerCase();
  const option = [...root.querySelectorAll<HTMLElement>('[role="option"]')].find((candidate) => (candidate.textContent ?? '').trim().toLowerCase().startsWith(wanted));
  if (!option) {
    log('option not found', label);
    return;
  }
  option.click();
  await pause(SETTLE_MS);
  input.blur();
}

/** The most months the calendar is paged through to reach a day. */
const MAX_MONTHS_PAGED = 24;

/** Opens the date picker under an anchor and picks a day, paging the calendar toward it. */
async function pickDate(target: string, daysFromToday: number): Promise<void> {
  const root = await waitFor(target);
  const trigger = root?.matches('button') ? root : root?.querySelector<HTMLElement>('button');
  if (!trigger) return;
  press(trigger);
  await pause(SETTLE_MS);
  const wanted = dayFromToday(daysFromToday);
  for (let paged = 0; paged <= MAX_MONTHS_PAGED; paged += 1) {
    const day = document.querySelector<HTMLButtonElement>(`[data-day="${wanted}"]:not([data-outside]) button`) ?? document.querySelector<HTMLButtonElement>(`[data-day="${wanted}"] button`);
    if (day) {
      day.click();
      await pause(SETTLE_MS);
      return;
    }
    const pager = document.querySelector<HTMLButtonElement>(daysFromToday < 0 ? '[aria-label*="previous month" i]' : '[aria-label*="next month" i]');
    if (!pager || pager.disabled) break;
    pager.click();
    await pause(MOUNT_POLL_MS);
  }
  log('date not found in the picker', wanted);
}

/** The app's open dialog, if any; the tour's own popover is a dialog too and does not count. */
function openDialog(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[role="dialog"]:not(.driver-popover), [role="alertdialog"]:not(.driver-popover)');
}

/** Closes whatever dialog is open: the X, Cancel, or Back. */
function closeDialog(): void {
  const dialog = openDialog();
  if (!dialog) return;
  const close =
    dialog.querySelector<HTMLElement>('[data-slot="dialog-close"], button[aria-label="Close"]') ??
    [...dialog.querySelectorAll<HTMLButtonElement>('button')].find((button) => /^(cancel|close|back|not now)$/i.test(button.textContent?.trim() ?? '')) ??
    null;
  close?.click();
}

/** The step (at or before `index`) that navigates, so a step deep in a flow knows its page. */
function pathFor(steps: readonly GuideStep[], index: number, fallback: string): string {
  for (let i = index; i >= 0; i -= 1) {
    const path = steps[i]?.path;
    if (path) return path;
  }
  return fallback;
}

/** Re-opens the dialog a step lives in when it has been closed, by replaying the nearest opener. */
function openerFor(steps: readonly GuideStep[], index: number): GuideStep | undefined {
  for (let i = index; i >= 0; i -= 1) {
    const step = steps[i];
    if (!step) break;
    if (step.click) return step;
    if (i < index && step.closeAfter) break;
  }
  return undefined;
}

async function clickThrough(targets: string | readonly string[]): Promise<boolean> {
  for (const target of Array.isArray(targets) ? targets : [targets]) {
    const trigger = await waitFor(target);
    if (!trigger) return false;
    press(trigger);
    await pause(SETTLE_MS);
  }
  return true;
}

async function prepare(guide: Guide, index: number, navigate: Navigate): Promise<HTMLElement | null> {
  const step = guide.steps[index];
  if (!step) return null;
  // A step says where it lives, or the tour's start does; the steps between
  // stay wherever the last click took them (a New button that opens a page).
  const wanted = step.path ?? (index === 0 ? guide.path : undefined);
  if (wanted && !samePlace(guidePath(wanted))) {
    navigate(guidePath(wanted));
    await pause(SETTLE_MS);
  }
  if (step.click) {
    if (!(await clickThrough(step.click))) {
      log(`step ${index + 1} skipped: opener not found`, step.click);
      return null;
    }
  } else if (!find(step.target) && !openDialog()) {
    // Coming back to a step inside a dialog or on a page a click opened,
    // with nothing open: get to its page, then replay whatever opened it.
    // With a dialog already open the anchor may only appear after the
    // select or typing below, and pressing an opener behind the dialog
    // would close it.
    const page = guidePath(pathFor(guide.steps, index, guide.path));
    if (!samePlace(page)) {
      navigate(page);
      await pause(SETTLE_MS);
    }
    const opener = openerFor(guide.steps, index);
    if (opener?.click && !find(step.target)) await clickThrough(opener.click);
  }
  if (step.select) {
    await waitFor(step.select.target);
    selectIn(step.select.target, step.select.value);
    await pause(SETTLE_MS);
  }
  if (step.type) {
    await waitFor(step.type.target);
    typeInto(step.type.target, step.type.text);
    await pause(SETTLE_MS);
  }
  if (step.choose) await chooseOption(step.choose.target, step.choose.option);
  if (step.date) await pickDate(step.date.target, step.date.daysFromToday);
  const element = await waitFor(step.target);
  if (!element) log(`step ${index + 1} skipped: anchor not found`, step.target);
  else log(`step ${index + 1} ready`, step.target);
  element?.scrollIntoView({ block: 'center', behavior: 'instant' });
  return element;
}

function popoverFor(step: GuideStep): { title: string; description: string; side?: GuideStep['side']; align?: GuideStep['align'] } {
  return { title: step.title, description: step.body, ...(step.side ? { side: step.side } : {}), ...(step.align ? { align: step.align } : {}) };
}

export async function runGuide(id: string, navigate: Navigate, currentPath: string): Promise<boolean> {
  if (!isGuideId(id)) return false;
  const guide = GUIDES[id];
  if (!isSandbox()) {
    // Remember where to return without the parameter that starts a tour,
    // or ending the tour would start it again.
    enterSandbox(withoutTourParams(currentPath));
    navigate(guidePath(guide.path));
    await pause(SETTLE_MS * 2);
  }

  let current = 0;
  let tour: Driver | null = null;
  let busy = false;

  /** Moves to the nearest step from `from` in `direction` whose anchor can be made to appear. */
  async function go(from: number, direction: 1 | -1): Promise<void> {
    if (busy) {
      log('busy, click ignored');
      return;
    }
    busy = true;
    try {
      let index = from;
      for (;;) {
        index += direction;
        const step = guide.steps[index];
        if (!step) {
          if (direction === 1) tour?.destroy();
          return;
        }
        // A dialog stays open only while the next step lives in it: same
        // opener, no closeAfter on the step being left. Anything else closes
        // it first, whichever way the person is moving.
        const leaving = guide.steps[current];
        // A click on something inside the open dialog (its submit, an Add
        // range button) stays in it too.
        const nextClick = Array.isArray(step.click) ? step.click[0] : step.click;
        const clicksInside = nextClick !== undefined && Boolean(openDialog()?.querySelector(selector(nextClick)));
        const sameDialog = clicksInside || (openerFor(guide.steps, index) === openerFor(guide.steps, current) && !leaving?.closeAfter);
        if (!sameDialog && openDialog()) {
          log(`closing dialog before step ${index + 1}`);
          closeDialog();
          await pause(SETTLE_MS);
        }
        const element = await prepare(guide, index, navigate);
        if (element) {
          current = index;
          tour?.moveTo(index);
          return;
        }
      }
    } catch (error) {
      log('failed', error);
    } finally {
      busy = false;
    }
  }

  const first = await prepare(guide, 0, navigate);
  if (!first) return false;
  const unshield = shieldPopover();

  tour = driver({
    showProgress: true,
    // allowClose is what makes driver.js render the X; the shield above
    // keeps the overlay click it would also enable from ending the tour.
    allowClose: true,
    showButtons: ['next', 'previous', 'close'],
    allowKeyboardControl: false,
    overlayColor: 'rgba(14, 46, 119, 0.55)',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    steps: guide.steps.map((step) => ({ element: selector(step.target), popover: popoverFor(step) })),
    onNextClick: () => {
      void go(current, 1);
    },
    onPrevClick: () => {
      void go(current, -1);
    },
    onCloseClick: () => {
      // The X: shut whatever the tour opened, then tear down (which drops the sandbox below).
      log('closing');
      if (openDialog()) closeDialog();
      tour?.destroy();
    },
    onDestroyStarted: () => {
      log('ending');
      if (openDialog()) closeDialog();
      tour?.destroy();
    },
    onDestroyed: () => {
      unshield();
      const returnTo = leaveSandbox();
      log('ended, returning to', returnTo);
      navigate(returnTo);
    },
  });
  tour.drive(0);
  return true;
}
